import React, { useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useEditFollowUp, useSendFollowUp, useSkipFollowUp, useConfirmFollowUpSent } from '../../hooks/useGuestReviews';
import { FOLLOWUP_STATUS, FOLLOWUP_PROBLEM_TEXT, FAILURE_TEXT, TEXT } from '../../config/guestReviewLabels';
import { StatusChip, ErrorLine, useRunner, textDir } from './shared';

/**
 * The PRIVATE message to a guest who complained in their private note (tasks/review-complaint-triage.md
 * T9–T11) — a conversation message on the booking's thread, never public. The AI drafts thanks + an
 * apology; ops adds what fits in their own words (fixed, noted, passed on…) and sends exactly what is
 * shown. Nothing is sent without this click (T1). The checks come from the server (`problems`).
 */

const SENDABLE = ['needs_approval', 'failed'];
const UNSENT = ['drafting', 'needs_approval', 'failed'];
// Nothing left to do — shown as one line.
const DONE = ['sent', 'skipped'];

const FollowUpBlock = ({ replyId, followUp, sendOn }) => {
  const [editing, setEditing] = useState(followUp.noDraft);
  const [text, setText] = useState(followUp.text || '');
  const [skipping, setSkipping] = useState(false);
  const edit = useEditFollowUp();
  const send = useSendFollowUp();
  const skip = useSkipFollowUp();
  const confirmSent = useConfirmFollowUpSent();
  const { error, run } = useRunner();

  const busy = edit.isPending || send.isPending || skip.isPending || confirmSent.isPending || followUp.status === 'sending';
  const unsent = UNSENT.includes(followUp.status);
  const problems = followUp.problems || [];
  const blocked = problems.length > 0;
  const canSend = sendOn && SENDABLE.includes(followUp.status) && !blocked;

  const save = () => run(edit, { id: replyId, text }, () => setEditing(false));
  const approve = (confirmUnknown = false) => run(send, { id: replyId, text: followUp.text, confirmUnknown });

  const done = DONE.includes(followUp.status);

  return (
    <section className={`gr-out gr-out--private ${done ? 'gr-out--done' : ''}`} aria-label={TEXT.followUpTitle}>
      <div className="gr-block-head">
        <div>
          <h4 className="gr-out-title"><i className="bi bi-lock-fill" aria-hidden="true"></i> {TEXT.followUpTitle}</h4>
          <div className="gr-out-where">{TEXT.followUpHint}</div>
        </div>
        <StatusChip status={followUp.status} labels={FOLLOWUP_STATUS} />
      </div>
      {done ? (
        (followUp.sentText || followUp.text) && <p className="gr-done-line" dir="auto">{followUp.sentText || followUp.text}</p>
      ) : (<>

      {followUp.status === 'drafting' && !editing && (
        <div className="gr-hint"><Spinner animation="border" size="sm" /> {TEXT.draftPending}</div>
      )}

      {editing ? (
        <div className="gr-edit">
          <Form.Control
            as="textarea"
            rows={4}
            className="touch-input gr-textarea"
            value={text}
            maxLength={700}
            disabled={busy}
            onChange={e => setText(e.target.value)}
            placeholder={TEXT.followUpPlaceholder}
            aria-label={TEXT.followUpPlaceholder}
            dir={textDir(text)}
          />
          <ErrorLine message={error} />
          <div className="gr-actions">
            <Button className="touch-btn gr-primary" onClick={save} disabled={busy || !text.trim()}>
              {edit.isPending ? <Spinner animation="border" size="sm" /> : TEXT.save}
            </Button>
            <Button variant="light" className="touch-btn" onClick={() => { setText(followUp.text || ''); setEditing(false); }} disabled={busy}>
              {TEXT.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Exactly what the guest will read. */}
          {(followUp.sentText || followUp.text) && (
            <blockquote className="gr-text gr-text--private" dir="auto">{followUp.sentText || followUp.text}</blockquote>
          )}
          {followUp.noDraft && <div className="gr-attention">{TEXT.followUpNoDraft}</div>}
          {unsent && followUp.text && !followUp.edited && <div className="gr-hint">{TEXT.followUpAddYourLine}</div>}
          {unsent && blocked && followUp.text && (
            <div className="gr-problems" role="alert">
              <p className="gr-problems-title">{TEXT.followUpProblemsTitle}</p>
              <ul>
                {problems.map(p => <li key={p}>{FOLLOWUP_PROBLEM_TEXT[p] || p}</li>)}
              </ul>
            </div>
          )}
          <ErrorLine message={error} />

          {unsent && (
            <div className="gr-actions">
              {SENDABLE.includes(followUp.status) && (
                <Button className="touch-btn gr-primary" onClick={() => approve()} disabled={busy || !canSend}>
                  {send.isPending ? <Spinner animation="border" size="sm" /> : TEXT.sendFollowUp}
                </Button>
              )}
              <Button variant="outline-secondary" className="touch-btn" onClick={() => { setText(followUp.text || ''); setEditing(true); }} disabled={busy}>
                {followUp.text ? TEXT.edit : TEXT.writeFollowUp}
              </Button>
              <Button variant="link" className="touch-btn gr-link" onClick={() => setSkipping(true)} disabled={busy || skipping}>
                {TEXT.skip}
              </Button>
            </div>
          )}

          {followUp.status === 'unknown' && (
            <div className="gr-actions">
              <Button className="touch-btn gr-primary" onClick={() => approve(true)} disabled={busy || !sendOn || blocked}>
                {TEXT.resendChecked}
              </Button>
              <Button variant="outline-secondary" className="touch-btn" onClick={() => run(confirmSent, { id: replyId })} disabled={busy}>
                {confirmSent.isPending ? <Spinner animation="border" size="sm" /> : TEXT.confirmFollowUpSent}
              </Button>
            </div>
          )}
        </>
      )}

      {unsent && skipping && (
        <div className="gr-confirm" role="group" aria-label={TEXT.skip}>
          <p className="gr-confirm-text">{TEXT.skipFollowUpConfirm}</p>
          <div className="gr-actions">
            <Button className="touch-btn gr-primary" onClick={() => run(skip, { id: replyId }, () => setSkipping(false))} disabled={busy}>
              {skip.isPending ? <Spinner animation="border" size="sm" /> : TEXT.confirm}
            </Button>
            <Button variant="light" className="touch-btn" onClick={() => setSkipping(false)} disabled={busy}>
              {TEXT.cancel}
            </Button>
          </div>
        </div>
      )}

      <div className="gr-details">
        {followUp.status === 'sending' && <span><Spinner animation="border" size="sm" /> {FOLLOWUP_STATUS.sending}</span>}
        {followUp.failure && ['failed', 'unknown'].includes(followUp.status) && (
          <span className="gr-failure">{FAILURE_TEXT[followUp.failure.code]}</span>
        )}
        {followUp.approvedByName && ['sending', 'sent'].includes(followUp.status) && <span>{TEXT.by} {followUp.approvedByName}</span>}
        {followUp.edited && <span>{TEXT.edited}</span>}
      </div>
      </>)}
    </section>
  );
};

export default FollowUpBlock;
